import React from 'react';
import { FiUsers, FiTrendingUp, FiCheckCircle, FiClock, FiShield, FiZap, FiBarChart2, FiAlertTriangle } from 'react-icons/fi';

const FacultyBurnout = () => {
  const burnoutIndicators = [
    {
      icon: <FiClock className="w-6 h-6" />,
      title: 'Excessive Workload Hours',
      description: 'Faculty teaching 20+ hours per week with additional administrative duties are at high risk of burnout. When workload exceeds capacity, quality suffers, health declines, and turnover increases. The data reveals this pattern early.',
      stat: 'Faculty with 20+ teaching hours are 3x more likely to experience burnout',
      solution: 'Truyon tracks teaching loads, administrative duties, and total working hours to identify overloaded faculty.',
      color: 'bg-indigo-50'
    },
    {
      icon: <FiAlertTriangle className="w-6 h-6" />,
      title: 'Declining Student Feedback Scores',
      description: 'When student satisfaction scores drop consistently, it often reflects faculty exhaustion. Burned out faculty can\'t maintain the energy, enthusiasm, or patience needed for effective teaching. This is a leading indicator of burnout.',
      stat: '70% of burnout cases show declining feedback scores for 6+ months',
      solution: 'Truyon tracks feedback trends and flags faculty with consistent declines.',
      color: 'bg-emerald-50'
    },
    {
      icon: <FiBarChart2 className="w-6 h-6" />,
      title: 'Increased Absenteeism',
      description: 'Faculty taking frequent sick leaves or unscheduled absences may be experiencing burnout symptoms. While some absence is normal, patterns of increased absenteeism signal underlying issues that need attention.',
      stat: 'Chronic absenteeism increases 40% before burnout manifests',
      solution: 'Truyon monitors attendance patterns and identifies concerning trends.',
      color: 'bg-amber-50'
    },
    {
      icon: <FiUsers className="w-6 h-6" />,
      title: 'Reduced Engagement in Department Life',
      description: 'Faculty who stop attending meetings, avoid collaboration, or withdraw from departmental activities may be disengaging due to burnout. Isolation is both a symptom and a cause of burnout.',
      stat: '75% of burned out faculty show reduced engagement within 3 months',
      solution: 'Truyon tracks participation and flags faculty showing declining engagement.',
      color: 'bg-rose-50'
    },
    {
      icon: <FiTrendingUp className="w-6 h-6" />,
      title: 'High Administrative Burden',
      description: 'Faculty spending disproportionate time on admin—paperwork, meetings, reports—rather than teaching or research face burnout risk. When admin work takes over, faculty lose connection to their purpose.',
      stat: '80% of faculty burnout cases involve excessive administrative load',
      solution: 'Truyon helps leaders balance teaching, research, and administrative responsibilities.',
      color: 'bg-purple-50'
    },
    {
      icon: <FiShield className="w-6 h-6" />,
      title: 'Increased Turnover Intent',
      description: 'Faculty discussing retirement, external opportunities, or expressing dissatisfaction may be signaling burnout. Often, faculty leave not for better pay but to escape unsustainable workloads.',
      stat: '60% of faculty departures are burnout-related, not compensation-related',
      solution: 'Truyon\'s retention analytics help identify faculty at risk of leaving.',
      color: 'bg-blue-50'
    }
  ];

  const preventionStrategies = [
    {
      title: 'Balance Workload Distribution',
      description: 'Ensure teaching loads, administrative duties, and research expectations are balanced across faculty.',
      icon: <FiZap className="w-5 h-5" />
    },
    {
      title: 'Track Early Warning Signs',
      description: 'Monitor attendance, feedback, engagement, and other indicators to identify burnout risks early.',
      icon: <FiBarChart2 className="w-5 h-5" />
    },
    {
      title: 'Create Support Systems',
      description: 'Establish mentoring programs, wellness initiatives, and professional development opportunities.',
      icon: <FiUsers className="w-5 h-5" />
    },
    {
      title: 'Regular Check-ins',
      description: 'Schedule one-on-one meetings with faculty to discuss workload, challenges, and well-being.',
      icon: <FiCheckCircle className="w-5 h-5" />
    },
    {
      title: 'Reduce Administrative Burden',
      description: 'Automate routine tasks and streamline processes so faculty can focus on teaching and research.',
      icon: <FiShield className="w-5 h-5" />
    },
    {
      title: 'Foster Community',
      description: 'Build a supportive departmental culture where faculty feel connected and valued.',
      icon: <FiTrendingUp className="w-5 h-5" />
    }
  ];

  return (
    <>
      {/* Introduction */}
      <p className="text-lg text-slate-600 leading-relaxed mb-6">
        Faculty burnout is one of the most serious challenges facing educational institutions today. It affects teaching quality, student outcomes, faculty health, and institutional stability. Burned out faculty can't be effective. They become disengaged, produce lower quality work, and eventually leave.
      </p>
      
      <p className="text-lg text-slate-600 leading-relaxed mb-6">
        The cost of faculty burnout is enormous. <strong className="text-slate-900">Replacing a burned-out faculty member can cost 50-100% of their annual salary.</strong> And the damage goes beyond money—students suffer, research suffers, and institutional reputation suffers.
      </p>

      <p className="text-lg text-slate-600 leading-relaxed mb-8">
        Data can help institutions spot faculty burnout before it happens. Here's how.
      </p>

      {/* Stats Grid */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-8">
        <div className="bg-white rounded-xl p-6 text-center border">
          <div className="text-3xl font-extrabold text-indigo-600">3x</div>
          <div className="text-sm text-slate-500">Higher burnout risk with 20+ teaching hours</div>
        </div>
        <div className="bg-white rounded-xl p-6 text-center border">
          <div className="text-3xl font-extrabold text-emerald-600">80%</div>
          <div className="text-sm text-slate-500">Burnout cases involve excessive admin load</div>
        </div>
        <div className="bg-white rounded-xl p-6 text-center border">
          <div className="text-3xl font-extrabold text-amber-600">60%</div>
          <div className="text-sm text-slate-500">Faculty departures are burnout-related</div>
        </div>
      </div>

      <h2 className="text-2xl font-bold text-slate-900 mb-6">Early Warning Signs of Faculty Burnout</h2>

      {/* Burnout Indicators */}
      {burnoutIndicators.map((item, i) => (
        <div key={i} className={`${item.color} rounded-2xl p-6 mb-6 border`}>
          <div className="flex items-start gap-4">
            <div className="bg-white rounded-xl p-3 shadow-sm flex-shrink-0 mt-1">
              {item.icon}
            </div>
            <div className="flex-1">
              <h3 className="text-xl font-bold text-slate-900 mb-2">{item.title}</h3>
              <p className="text-slate-600 leading-relaxed text-sm mb-4">{item.description}</p>
              <div className="bg-white/60 rounded-xl p-3 mb-3 border border-amber-200">
                <p className="text-sm font-semibold text-amber-600">📊 {item.stat}</p>
              </div>
              <div className="bg-white rounded-xl p-4 border border-emerald-200">
                <div className="flex items-center gap-2 text-sm font-semibold text-emerald-600 mb-1">
                  <FiCheckCircle className="w-4 h-4" />
                  <span>How Truyon Helps</span>
                </div>
                <p className="text-sm text-slate-700">{item.solution}</p>
              </div>
            </div>
          </div>
        </div>
      ))}

      {/* Prevention Strategies */}
      <div className="mt-8 bg-white rounded-2xl border border-slate-200 p-6 sm:p-8">
        <h3 className="text-xl font-bold text-slate-900 mb-4">How to Prevent Faculty Burnout</h3>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          {preventionStrategies.map((item, i) => (
            <div key={i} className="bg-indigo-50 rounded-xl p-4 border border-indigo-200">
              <div className="flex items-start gap-3">
                <div className="bg-white rounded-lg p-2 flex-shrink-0">
                  {item.icon}
                </div>
                <div>
                  <h4 className="font-bold text-slate-900 text-sm">{item.title}</h4>
                  <p className="text-xs text-slate-600 mt-1">{item.description}</p>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Comparison Section */}
      <div className="mt-8 bg-white rounded-2xl border border-slate-200 p-6 sm:p-8">
        <h3 className="text-xl font-bold text-slate-900 mb-4">Reactive vs. Data-Driven Faculty Support</h3>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div className="bg-red-50 rounded-xl p-4 border border-red-200">
            <div className="text-sm font-semibold text-red-700 mb-2">Reactive Approach</div>
            <ul className="text-sm text-slate-600 space-y-2">
              <li className="flex items-start gap-2">• <span>Respond after burnout occurs</span></li>
              <li className="flex items-start gap-2">• <span>No visibility into faculty well-being</span></li>
              <li className="flex items-start gap-2">• <span>React to departures and grievances</span></li>
              <li className="flex items-start gap-2">• <span>High turnover and replacement costs</span></li>
              <li className="flex items-start gap-2">• <span>Demoralized faculty and students</span></li>
            </ul>
          </div>
          <div className="bg-emerald-50 rounded-xl p-4 border border-emerald-200">
            <div className="text-sm font-semibold text-emerald-700 mb-2">Data-Driven Prevention</div>
            <ul className="text-sm text-slate-600 space-y-2">
              <li className="flex items-start gap-2">• <span>Identify burnout risk early</span></li>
              <li className="flex items-start gap-2">• <span>Real-time visibility into faculty well-being</span></li>
              <li className="flex items-start gap-2">• <span>Proactive support and intervention</span></li>
              <li className="flex items-start gap-2">• <span>Lower turnover and retention costs</span></li>
              <li className="flex items-start gap-2">• <span>Engaged, supported faculty</span></li>
            </ul>
          </div>
        </div>
      </div>

      {/* Why Truyon Section */}
      <div className="mt-8 bg-indigo-50 rounded-2xl p-6 border border-indigo-200">
        <h3 className="text-lg font-bold text-indigo-900 mb-3">Why Truyon is the Best Choice for Faculty Support</h3>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          <div className="bg-white rounded-xl p-4 border border-indigo-200 text-center">
            <div className="text-2xl mb-1">📊</div>
            <h4 className="font-bold text-slate-900 text-sm">Workload Tracking</h4>
            <p className="text-xs text-slate-600">Monitor teaching loads, admin duties, and research time</p>
          </div>
          <div className="bg-white rounded-xl p-4 border border-indigo-200 text-center">
            <div className="text-2xl mb-1">🚨</div>
            <h4 className="font-bold text-slate-900 text-sm">Early Warning Alerts</h4>
            <p className="text-xs text-slate-600">Flag faculty showing burnout risk indicators</p>
          </div>
          <div className="bg-white rounded-xl p-4 border border-indigo-200 text-center">
            <div className="text-2xl mb-1">📈</div>
            <h4 className="font-bold text-slate-900 text-sm">Feedback Analytics</h4>
            <p className="text-xs text-slate-600">Track student feedback trends over time</p>
          </div>
          <div className="bg-white rounded-xl p-4 border border-indigo-200 text-center">
            <div className="text-2xl mb-1">🔍</div>
            <h4 className="font-bold text-slate-900 text-sm">Retention Insights</h4>
            <p className="text-xs text-slate-600">Identify faculty at risk of leaving</p>
          </div>
          <div className="bg-white rounded-xl p-4 border border-indigo-200 text-center">
            <div className="text-2xl mb-1">📱</div>
            <h4 className="font-bold text-slate-900 text-sm">Mobile Access</h4>
            <p className="text-xs text-slate-600">Leaders can monitor faculty well-being anywhere</p>
          </div>
          <div className="bg-white rounded-xl p-4 border border-indigo-200 text-center">
            <div className="text-2xl mb-1">🤝</div>
            <h4 className="font-bold text-slate-900 text-sm">Collaborative Support</h4>
            <p className="text-xs text-slate-600">Track interventions and support progress</p>
          </div>
        </div>
      </div>

      {/* The Human Impact */}
      <div className="mt-8 bg-amber-50 rounded-2xl p-6 border border-amber-200">
        <h3 className="text-lg font-bold text-amber-900 mb-3">The Human Cost of Faculty Burnout</h3>
        <p className="text-amber-800 leading-relaxed mb-3">
          Behind every burnout statistic is a person. A dedicated teacher who can't find the energy to prepare lessons. A researcher who's lost their passion. A mentor who's too exhausted to support students. A faculty member who's thinking about leaving the profession they once loved.
        </p>
        <p className="text-amber-800 leading-relaxed mb-3">
          Burnout doesn't just affect faculty—it affects everyone. Students receive lower quality education. Research output declines. Colleagues have to pick up the slack. The entire institution suffers.
        </p>
        <p className="text-amber-800 leading-relaxed">
          <strong>Supporting faculty well-being isn't just good for them—it's good for everyone.</strong>
        </p>
      </div>

      {/* Conclusion */}
      <div className="mt-8 bg-indigo-50 rounded-2xl p-6 border border-indigo-200">
        <h3 className="text-lg font-bold text-indigo-900 mb-2">Support Your Faculty with Truyon</h3>
        <p className="text-indigo-800 leading-relaxed">
          Faculty burnout is preventable—but only if you can see it coming. Data gives you the visibility you need to identify early warning signs, intervene before burnout occurs, and support your faculty effectively.
        </p>
        <p className="text-indigo-800 leading-relaxed mt-3">
          Truyon provides the analytics and insights you need to monitor faculty well-being. Workload tracking, engagement analytics, feedback trends, and retention insights—all in one platform.
        </p>
        <p className="text-indigo-800 leading-relaxed mt-3">
          <strong>Truyon for faculty support.</strong> Prevent burnout. Retain talent. Support your people.
        </p>
      </div>
    </>
  );
};

export default FacultyBurnout;